import type { FeedbackDecision, ReviewDecision } from '../../lib/shortlists/types';
import {
  buildPublicShortlistPath,
  buildPublicShortlistUrl,
} from '../../lib/shortlists/routing';

export const SHORTLIST_WEB_CONTENT_MAX_WIDTH = 680;

export interface RelativeTimeValue {
  unit: 'justNow' | 'minutes' | 'hours' | 'days' | 'weeks';
  count: number;
}

export function relativeTimeValue(isoDate: string, nowDate = new Date()): RelativeTimeValue {
  const diffMs = Math.max(nowDate.getTime() - new Date(isoDate).getTime(), 0);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;

  if (diffMs < minute) {
    return { unit: 'justNow', count: 0 };
  }

  if (diffMs < hour) {
    return { unit: 'minutes', count: Math.max(1, Math.floor(diffMs / minute)) };
  }

  if (diffMs < day) {
    return { unit: 'hours', count: Math.max(1, Math.floor(diffMs / hour)) };
  }

  if (diffMs < week) {
    return { unit: 'days', count: Math.max(1, Math.floor(diffMs / day)) };
  }

  return { unit: 'weeks', count: Math.max(1, Math.floor(diffMs / week)) };
}

export function feedbackDecisionTone(decision: FeedbackDecision) {
  if (decision === 'yes') {
    return {
      text: '#059669',
      bg: 'rgba(16, 185, 129, 0.12)',
    };
  }

  return {
    text: '#DC2626',
    bg: 'rgba(239, 68, 68, 0.12)',
  };
}

export function reviewDecisionTone(decision: ReviewDecision) {
  if (decision === 'shortlisted') {
    return {
      text: '#059669',
      bg: 'rgba(16, 185, 129, 0.12)',
      border: 'rgba(16, 185, 129, 0.24)',
    };
  }

  if (decision === 'passed') {
    return {
      text: '#DC2626',
      bg: 'rgba(239, 68, 68, 0.1)',
      border: 'rgba(239, 68, 68, 0.24)',
    };
  }

  return {
    text: '#6B7280',
    bg: 'rgba(107, 114, 128, 0.12)',
    border: 'rgba(107, 114, 128, 0.24)',
  };
}

export function publicShortlistUrl(slug: string) {
  const configured = buildPublicShortlistUrl(slug);
  if (configured) return configured;

  // Fall back to the current origin on web so the share button works in
  // local/preview environments where EXPO_PUBLIC_PUBLIC_WEB_ENABLED is not
  // set. Recipients open the link on the same Expo Router web build.
  if (
    typeof window !== 'undefined' &&
    typeof window.location?.origin === 'string'
  ) {
    const path = buildPublicShortlistPath(slug);
    if (path) return `${window.location.origin}${path}`;
  }

  return null;
}
